import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Container, PageHeader, Spinner } from "@/components/shared";
import { toast } from "sonner";
import { Users, BadgeCheck } from "lucide-react";

export default function Partners() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partners");
      setPartners(data.partners); setCategories(data.categories);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = cat ? partners.filter((p) => (p.categories || []).includes(cat)) : partners;
  const catMeta = categories.find((c) => c.key === cat);

  return (
    <Container>
      <PageHeader eyebrow="🤝🏿 Écosystème BIZARRE AI" title="Nos professionnels"
        subtitle="Des professionnels sélectionnés pour leur sérieux, leur expertise et leurs valeurs — pour transmettre, accompagner et protéger." />

      <div className="flex flex-wrap gap-2 mb-8" data-testid="partners-categories">
        <button data-testid="partners-cat-all" onClick={() => setCat("")}
          className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${!cat ? "border-gold/50 bg-gold/10 text-gold" : "border-white/12 text-white/60 hover:text-white"}`}>
          Tout ({partners.length})
        </button>
        {categories.map((c) => (
          <button key={c.key} data-testid={`partners-cat-${c.key}`} onClick={() => setCat(c.key)}
            className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${cat === c.key ? "border-gold/50 bg-gold/10 text-gold" : "border-white/12 text-white/60 hover:text-white"}`}>
            {c.emoji} {c.label} ({c.count})
          </button>
        ))}
      </div>

      {loading && <Spinner label="Chargement..." />}

      {!loading && shown.length === 0 && (
        <div className="card-surface p-12 text-center" data-testid="partners-empty">
          <Users size={30} className="text-gold/60 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-white/55">De nouveaux partenaires seront bientôt ajoutés{catMeta ? ` dans « ${catMeta.label} »` : ""}.</p>
        </div>
      )}

      {!loading && shown.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partners-grid">
          {shown.map((p) => (
            <button key={p.partner_id} data-testid={`partner-card-${p.partner_id}`} onClick={() => navigate(`/partenaires/${p.partner_id}`)}
              className="group card-surface p-5 text-left hover:border-gold/30 transition-colors flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl overflow-hidden border border-white/10 shrink-0 bg-white/[0.03]">
                {p.photo_url
                  ? <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
                  : <div className="w-full h-full flex items-center justify-center"><Users className="text-white/20" size={26} /></div>}
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-lg leading-tight group-hover:text-gold transition-colors">{p.name}</h3>
                <p className="text-white/45 text-sm line-clamp-2 mt-0.5">{p.role}</p>
                {p.specialties && <p className="text-white/35 text-xs mt-1 line-clamp-1">{p.specialties}</p>}
                {p.badge && <span className="inline-flex items-center gap-1 text-gold/90 text-[11px] mt-2"><BadgeCheck size={12} /> Partenaire</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </Container>
  );
}
